import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import ZonePricingPlanFormModal from 'src/features/zone/components/form/ZonePricingPlanFormModal';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { useCreateZonePricingPlan } from 'src/features/zone/api/useCreateZonePricingPlan';
import { LIST_ZONE_PRICING_PLANS_RESOURCE } from 'src/features/zone/api/useListZonePricingPlans';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan?: PricingPlan;
    children?: React.ReactNode;
}

export default function CreateZonePricingPlanFormModal<TData>({
    className,
    semantics,
}: PricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { zoneId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateZonePricingPlan({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ZONE_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccess),
                description: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <ZonePricingPlanFormModal
            className={className}
            mutate={(data, onSuccess, onError) => mutate({ ...data, zone: zoneId, parkingId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.addPricingPlanSubtitle)}
        />
    );
}
